import { Grid, Table, TableBody, TableContainer } from '@mui/material';
import { useAtom } from 'jotai';

import useIngestFromInheritance from '../CloudSourceHooks/useIngestFromInheritance.hooks';
import useSiteNameInheritance from '../CloudSourceHooks/useSiteNameInheritance.hooks';
import useSourceDataProviderActiveState from '../CloudSourceHooks/useSourceDataProviderActiveState';
import { sourceDialogModeAtom } from '../SourceCreate.store';
import type { DataSourceModel } from '../SourceCreate.types';
import useStyles from './DataProvider.styles';
import type { DataProviderProps } from './DataProvider.types';
import DataSourceRow from './DataSourceRow';

/**
 * Constructs a nested form field path for hierarchical data sources.
 *
 * Data providers are organized in a tree structure where each level
 * is separated by dots (e.g., "data_provider.AWSGuardDuty.SecurityHub").
 * This function builds the full path needed for React Hook Form registration.
 *
 * @param parentPath - The parent field path in the form hierarchy
 * @param dataSourceName - The current data source name to append
 * @returns The complete dot-notation path for this data source's form fields
 *
 * @example
 * buildFieldPath('data_provider', 'AWSGuardDuty') // => 'data_provider.AWSGuardDuty'
 * buildFieldPath('data_provider.AWSGuardDuty', 'SecurityHub') // => 'data_provider.AWSGuardDuty.SecurityHub'
 */
const buildFieldPath = (parentPath: string, dataSourceName: string): string => {
  return parentPath ? `${parentPath}.${dataSourceName}` : dataSourceName;
};

/**
 * Recursive component that renders hierarchical data source configurations.
 *
 * **What are Data Providers?**
 * Data providers represent different log sources or sub-sources within a parent
 * source type. For example:
 * - AWS S3 might have providers for CloudTrail, GuardDuty, DNS logs, etc.
 * - Microsoft sources have providers for O365, Defender, Entra ID Graph/O365 APIs
 * - Google Cloud has providers for different storage directories
 *
 * **Recursive Structure:**
 * This component can nest infinitely. A data source can contain child data_sources,
 * each rendered by another DataProvider instance. This creates a tree structure
 * where each row can expand to show its children.
 *
 * **Field Inheritance (Microsoft sources only):**
 * Three inheritance behaviors automatically sync values from the root form
 * to child data providers:
 *
 * 1. **Site Name Inheritance** (`useSiteNameInheritance`):
 *    - New mode: All providers inherit root site_name
 *    - Edit mode: Only newly added providers inherit; existing ones keep their values
 *    - User manual edits always take precedence
 *
 * 2. **Ingest From Date Inheritance** (`useIngestFromInheritance`):
 *    - When common_ingest_from is set, propagates to all selected providers
 *    - Skips providers that were manually edited or already saved
 *    - Detects conflicts (different dates) and shows UI warnings
 *
 * 3. **Active State Synchronization** (`useSourceDataProviderActiveState`):
 *    - Parent "active" = true only if at least one child is active
 *    - Parent toggle on/off cascades to all children
 *    - Child toggle updates parent to "any child active" state
 *
 * @param parent_name - The form field path prefix (e.g., "data_provider")
 * @param data_sources - Array of data source configurations to render
 */
const DataProvider = ({
  parent_name = 'data_provider',
  data_sources
}: DataProviderProps) => {
  const { classes } = useStyles();
  const [mode] = useAtom(sourceDialogModeAtom);

  // Apply field inheritance hooks (Microsoft sources only, mode-dependent)
  useSiteNameInheritance(mode);
  useIngestFromInheritance(mode);
  useSourceDataProviderActiveState();

  return (
    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
      <TableContainer className={classes.dpTableContainer}>
        <Table className={classes.dpTable} aria-label="collapsible table">
          <TableBody>
            {data_sources?.map((dataSource: DataSourceModel) => {
              // Build the full form field path for this data source
              // Example: "data_provider.AWSGuardDuty.SecurityHub"
              const fieldPath = buildFieldPath(
                parent_name,
                dataSource.data_source
              );

              return (
                <DataSourceRow
                  key={fieldPath}
                  parent_name={fieldPath}
                  DataProvider={DataProvider}
                  data_source={dataSource}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default DataProvider;
